import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { TrendingUp, ArrowLeft, CheckCircle } from "lucide-react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

export default function ResetPassword() {
  const { t } = useI18n();
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("reset_mismatch_error"));
      return;
    }
    if (password.length < 6) {
      setError(t("reset_length_error"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(t("reset_done_title"));
    } catch (er) {
      setError(formatApiError(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app">
      <TopBar />
      <div className="flex items-center justify-center min-h-[calc(100vh-56px)] px-4 py-8 radial-glow">
        <div className="w-full max-w-md bg-panel border border-line rounded-sm p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-buy flex items-center justify-center rounded-sm">
              <TrendingUp className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold">IQ<span className="text-buy">Trade</span></span>
          </div>

          {!done ? (
            <>
              <h1 className="text-2xl font-bold mb-2 tracking-tight">{t("reset_title")}</h1>
              <p className="text-sm text-muted mb-6">
                {t("reset_desc")}
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-dim">{t("reset_new_password")}</label>
                  <input
                    type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full mt-1.5 bg-app border border-line px-3 py-2.5 rounded-sm outline-none focus:border-buy"
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-dim">{t("reset_confirm_password")}</label>
                  <input
                    type="password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full mt-1.5 bg-app border border-line px-3 py-2.5 rounded-sm outline-none focus:border-buy"
                    minLength={6}
                  />
                </div>
                {error && <div className="text-sm text-sell font-mono">{error}</div>}
                <button disabled={busy} type="submit"
                  className="w-full bg-buy text-black font-bold py-3 rounded-sm hover:bg-emerald-400 disabled:opacity-50 uppercase tracking-widest text-sm">
                  {busy ? t("reset_resetting") : t("reset_button")}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-buy/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-buy" />
              </div>
              <h1 className="text-2xl font-bold mb-2">{t("reset_done_title")}</h1>
              <p className="text-sm text-muted mb-6">
                {t("reset_done_desc")}
              </p>
              <Link to="/login"
                className="inline-block w-full bg-buy text-black font-bold py-3 rounded-sm hover:bg-emerald-400 uppercase tracking-widest text-sm text-center">
                {t("login")}
              </Link>
            </div>
          )}

          {!done && (
            <div className="mt-6 text-sm text-muted text-center">
              <Link to="/login" className="inline-flex items-center gap-1 text-buy hover:underline">
                <ArrowLeft className="w-4 h-4" /> {t("back_to_login")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
